import { useCallback, useEffect, useRef, useState } from "react";
import {
  buildRemoteState,
  PHONE_RELAY_STATUS,
  PHONE_RELAY_STOP_TIMEOUT_MS,
  PHONE_RELAY_STOP_FLUSH_MS,
  clearRelayTranscript,
  createPhoneRelay,
  deletePhoneRelay,
  rearmPhoneRelay,
  requestPhoneStop,
  subscribePhoneRelay,
  writeClarifyingQuestions,
  writeRemoteState,
} from "services/chartMindPhoneRelayService";
import { createDeviceLinkGrant } from "services/deviceLinkService";

// Re-mint this far before the grant's own expiry, so a phone that scans at the last moment
// still has a live credential by the time the callable answers. Comfortably inside the
// two-minute GRANT_TTL_MS in functions/src/auth/deviceLink.js, which is the clock that counts.
const GRANT_REFRESH_MARGIN_MS = 20 * 1000;
// What to assume when the callable answers without an `expiresAt`. Shorter than the server's
// own TTL on purpose: refreshing early costs one callable, refreshing late costs a sign-in.
const GRANT_ASSUMED_TTL_MS = 2 * 60 * 1000;
// ⚠️ THE FLOOR THAT MAKES A REFRESH LOOP IMPOSSIBLE. A grant whose refresh time is already in
// the past — a clock skewed forward, a stub with no expiry — would otherwise re-mint on every
// render, in a tight loop, against a callable. With a grant in hand the next mint is always at
// least this far away, so the worst a bad timestamp can do is refresh more often than needed.
const GRANT_MIN_REFRESH_MS = 15 * 1000;
// Minting can fail for reasons a timer will never clear (a guest account, an MFA challenge this
// desktop never cleared). Try a couple of times for a cold start or a blip, then stop: the code
// works without a grant, it just costs one manual sign-in on a phone that has no session.
const GRANT_MINT_MAX_FAILURES = 3;

/**
 * Desktop half of the phone microphone relay.
 *
 * Arms a pairing, watches it, mirrors the phone's transcript into the ChartMind session as it
 * arrives, and blanks the wire once it holds the text.
 *
 * ⚠️ A PAIRING IS A WIRE, NOT A RECORDING, and confusing the two is what made the clinician
 * scan a fresh QR for every encounter. One pairing lasts as long as this tab, and carries as
 * many rounds as the clinician records — see start() and the round base below.
 *
 * @param {Object}   options
 * @param {string}   options.ownerUid    the signed-in clinician; both ends must be this uid
 * @param {string}   options.language    recognition locale, pinned at create (the phone has no
 *                                       access to the desktop's localStorage copy)
 * @param {Function} options.getRoundTarget  read ONCE as a round begins: where this round's text
 *                                       is going. `{kind:'encounter'}` or
 *                                       `{kind:'section', key, title, seed}`. Echoed back on
 *                                       both callbacks so the caller routes without guessing.
 * @param {Function} options.getBaseTranscript receives the round's target and returns what this
 *                                       round appends to — the encounter so far, or the text
 *                                       already in the section being dictated into
 * @param {Function} options.onTranscript called with the relayed text and the round's target on
 *                                       every update
 * @param {Function} options.onFinished   called once with the final text when the phone stops,
 *                                       plus `{timedOut, target}`
 */
const usePhoneRecordingRelay = ({
  ownerUid,
  language,
  getRoundTarget,
  getBaseTranscript,
  onTranscript,
  onFinished,
}) => {
  const [pairingId, setPairingId] = useState(null);
  const [status, setStatus] = useState(null);
  const [phoneClaimedAt, setPhoneClaimedAt] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  // The device-link grant that rides in the QR next to the pairing id, so an unsigned-in phone
  // needs no typing. Null when minting failed or was not possible; the QR copes.
  const [grant, setGrant] = useState(null);
  // A mint is in flight. Read by the dialog, which waits for it rather than showing a code
  // that carries no sign-in credential yet — see PhoneRecordingDialog.
  const [grantPending, setGrantPending] = useState(false);
  // Minting has failed enough times to look permanent (MFA never cleared on this desktop, a
  // guest account, the callable down). The distinction the dialog needs: "not yet" is worth
  // waiting for, "never" is not, and only one of them should put a grant-less code on screen.
  const [grantUnavailable, setGrantUnavailable] = useState(false);

  // The terminal payload, handed to an effect of its own rather than consumed in the snapshot
  // callback. See the effect below for why that separation is load-bearing.
  const [finalPayload, setFinalPayload] = useState(null);

  /**
   * A press of the phone's Next button, waiting to be acted on.
   *
   * Surfaced as state rather than fired through a callback because the thing that has to run it
   * — the current step's onNext — is rebuilt on every ChartMind render, and a callback captured
   * at subscribe time would advance to whatever step was current when the phone paired.
   */
  const [pendingCommand, setPendingCommand] = useState(null);
  // The last command id acted on, so a snapshot replayed for an unrelated reason (a reconnect,
  // the desktop's own clarifying-questions write) cannot advance a step a second time.
  const lastCommandIdRef = useRef(null);
  // The last remote state actually written, so the step push does not re-send an identical
  // payload on every keystroke of the encounter.
  const lastRemoteJsonRef = useRef(null);
  // How many remote writes are still in flight. The document is only worth checking against
  // what we last sent once our own writes have settled — see the correction in the snapshot
  // handler, which would otherwise fight a write that simply had not landed yet.
  const remoteWriteInFlightRef = useRef(0);
  // The remote itself, so the correction above can re-send it without waiting for a render.
  const lastRemoteRef = useRef(null);

  // StrictMode double-invokes effects in development, and a second create would orphan the
  // first pairing behind a QR nobody is looking at. Same guard as LinkDevicePage's DesktopView.
  // Strictly "a create is in flight" — an EXISTING pairing is not a reason to refuse, it is a
  // reason to reuse, which start() handles before it ever gets here.
  const startingRef = useRef(false);
  const stopTimerRef = useRef(null);
  // A `stopping` this desktop wrote itself, waiting to come back as a snapshot. See the stop
  // timer in the subscription: our own write must not be mistaken for the phone answering it.
  const selfStopEchoRef = useRef(false);
  // Consecutive failed mints for the current pairing. Minting is best effort and some of the
  // reasons it fails do not fix themselves on a timer (a guest account, an MFA challenge this
  // desktop never cleared), so the refresh below gives up rather than retrying forever.
  const grantFailuresRef = useRef(0);
  // Which pairing a mint belongs to, counted rather than named. The mint now starts BESIDE the
  // pairing write instead of after it (see createPairing), so at the moment it is fired there is
  // no id yet to key it on — and a code cancelled and re-opened in the meantime must still not
  // have the previous one's credential dropped into its QR.
  const grantGenRef = useRef(0);

  // The last transcript the phone put on the document, so a stop that times out still has
  // something to hand over. Declared up here because reset() below clears it.
  const latestTranscriptRef = useRef("");

  // Mirrors `pairingId` for the abandon-on-teardown handler at the bottom, which must not
  // re-register every time the status changes. Cleared by whoever consumes or cancels the
  // pairing, so teardown cannot delete a document that has already been dealt with.
  const pairingIdRef = useRef(null);
  // Same again for the status, which start() has to read without being rebuilt by every
  // snapshot the phone sends.
  const statusRef = useRef(null);

  // consumedRoundRef stops one round of recording being applied twice.
  const consumedRoundRef = useRef(false);

  /**
   * THE ROUND BASE: everything said before the phone tapped record this time.
   *
   * ⚠️ WITHOUT THIS, A SECOND ROUND DELETES THE FIRST. The wire carries only the current
   * round, and onTranscript replaces the session transcript wholesale — that is the only shape
   * updateTranscript has. So the desktop reads the encounter as it stands the moment a round
   * begins, keeps it here, and stitches each flush onto it.
   *
   * It lives on the desktop rather than on the wire on purpose. The phone used to seed the
   * earlier rounds back in from its own memory, which meant a phone reloaded mid-encounter
   * seeded nothing and silently wiped the encounter off the desktop on its next round — and it
   * kept the whole transcript sitting on a Firestore document for as long as the pairing lived.
   *
   * `roundActiveRef` is what makes it capture ONCE per round: re-reading it on a later flush
   * would stitch the round onto a base that already contains it.
   */
  const roundBaseRef = useRef("");
  const roundActiveRef = useRef(false);

  /**
   * WHERE THIS ROUND'S TEXT IS GOING, captured with the base and for the same reason.
   *
   * ⚠️ READ ONCE, AT THE START OF THE ROUND, NEVER AT THE END. A dictation is armed by opening a
   * note section's editor on the desktop and cancelled by closing it — so by the time a round
   * stops, the target the caller would report may already be a different one, or none. Reading
   * it late would post an addendum into whichever section happened to be open when the phone
   * finished draining, which is a silent way to write into the wrong part of a clinical note.
   *
   * The default is the encounter, so a relay wired up without ever setting a target behaves
   * exactly as it did before this existed.
   */
  const roundTargetRef = useRef({ kind: "encounter" });

  // Callbacks in refs: they change identity on every ChartMind render, and the subscription
  // effect must not tear down and re-subscribe every time that happens.
  const onTranscriptRef = useRef(onTranscript);
  const onFinishedRef = useRef(onFinished);
  const getBaseTranscriptRef = useRef(getBaseTranscript);
  const getRoundTargetRef = useRef(getRoundTarget);
  useEffect(() => {
    onTranscriptRef.current = onTranscript;
    onFinishedRef.current = onFinished;
    getBaseTranscriptRef.current = getBaseTranscript;
    getRoundTargetRef.current = getRoundTarget;
  }, [onTranscript, onFinished, getBaseTranscript, getRoundTarget]);

  /** Stitch a round's text onto what was already said. */
  const composeRound = useCallback(
    (roundText) =>
      [roundBaseRef.current, roundText]
        .filter((part) => part && part.trim())
        .join(" ")
        .trim(),
    [],
  );

  const clearStopTimer = useCallback(() => {
    if (stopTimerRef.current) {
      clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
  }, []);

  /**
   * Arm the "give up on the phone" timer.
   *
   * Two windows, one timer. PHONE_RELAY_STOP_TIMEOUT_MS is the short one, waiting for the phone
   * to say anything at all; PHONE_RELAY_STOP_FLUSH_MS is the long one, granted once it has, so
   * it can finish transcribing what it already recorded. See the constants for why one number
   * cannot do both jobs.
   */
  const armStopTimer = useCallback(
    (id, ms) => {
      clearStopTimer();
      stopTimerRef.current = setTimeout(() => {
        setFinalPayload({ pairingId: id, transcript: null, timedOut: true });
      }, ms);
    },
    [clearStopTimer],
  );

  const reset = useCallback(() => {
    clearStopTimer();
    selfStopEchoRef.current = false;
    consumedRoundRef.current = false;
    roundActiveRef.current = false;
    roundBaseRef.current = "";
    setPairingId(null);
    statusRef.current = null;
    // Belongs to the pairing that just ended: leaving it would let a stop that times out on the
    // NEXT pairing, before its first snapshot lands, consume the previous encounter's text.
    latestTranscriptRef.current = "";
    // Drop the grant with the pairing. It is single-use and two-minute-lived anyway, but there
    // is no reason to keep an unspent credential in React state after the QR comes down.
    setGrant(null);
    setGrantPending(false);
    setGrantUnavailable(false);
    grantFailuresRef.current = 0;
    // Anything still in flight now belongs to a pairing that no longer exists.
    grantGenRef.current += 1;
    setStatus(null);
    setPhoneClaimedAt(null);
    setFinalPayload(null);
    setPendingCommand(null);
    lastCommandIdRef.current = null;
    // Belongs to the pairing that just ended. Left set, the first push to the NEXT pairing
    // would be skipped as a duplicate and that phone would sit with no Next button at all.
    lastRemoteJsonRef.current = null;
    lastRemoteRef.current = null;
  }, [clearStopTimer]);

  /**
   * Mint a device-link grant for a pairing, if that pairing is still the live one.
   *
   * Guarded on the generation rather than fired and forgotten: a code cancelled and re-opened
   * in the meantime must not have a credential minted for the previous one dropped into its QR.
   */
  const mintGrant = useCallback((gen) => {
    setGrantPending(true);
    return createDeviceLinkGrant()
      .then(({ grantId, grantSecret, expiresAt }) => {
        if (grantGenRef.current !== gen) return;
        grantFailuresRef.current = 0;
        setGrant({
          grantId,
          grantSecret,
          refreshAt:
            (expiresAt || Date.now() + GRANT_ASSUMED_TTL_MS) - GRANT_REFRESH_MARGIN_MS,
        });
      })
      .catch(() => {
        // The refresh below retries a couple of times for a cold start or a blip, then stops.
        // Only at that point is the QR allowed up without a credential in it — see the dialog.
        if (grantGenRef.current !== gen) return;
        grantFailuresRef.current += 1;
        if (grantFailuresRef.current >= GRANT_MINT_MAX_FAILURES) setGrantUnavailable(true);
      })
      .finally(() => {
        // A stale mint must not clear the pending flag of the one that replaced it.
        if (grantGenRef.current === gen) setGrantPending(false);
      });
  }, []);

  /**
   * Keep the grant in the QR alive for as long as the QR is on screen.
   *
   * ⚠️ THE CODE OUTLIVES THE CREDENTIAL IN IT, which is what made "Record on your phone" send
   * people to a manual sign-in at random. A grant lasts GRANT_TTL_MS — two minutes, and
   * deliberately short because it is a bearer credential sitting in visible light. The pairing
   * it rides with lasts two hours and is reused across encounters, and a code nobody has
   * scanned is deliberately left up rather than replaced. So any scan more than two minutes
   * after the dialog opened redeemed a grant the server had already expired, and the phone
   * fell through to "Please sign in first" with nothing on either screen explaining why.
   *
   * Re-minting before it lapses is the fix rather than a longer TTL: the code stays live for
   * however long it is displayed, and the window in which a photograph of the screen is worth
   * anything stays at two minutes instead of growing to match.
   *
   * WAITING is exactly "a code is up that nobody has scanned" — the only state where a QR is
   * being displayed. Once a phone joins, the code comes down and its grant is already spent.
   */
  useEffect(() => {
    if (status !== PHONE_RELAY_STATUS.WAITING || !pairingId) return undefined;
    if (grantPending || grantFailuresRef.current >= GRANT_MINT_MAX_FAILURES) return undefined;

    if (!grant) {
      // A retry, not the first mint: createPairing fires that one alongside the pairing write.
      // Reached when a mint failed, or when the code is a reused one this tab already had up.
      mintGrant(grantGenRef.current);
      return undefined;
    }
    const dueIn = Math.max(grant.refreshAt - Date.now(), GRANT_MIN_REFRESH_MS);
    const timer = setTimeout(() => mintGrant(grantGenRef.current), dueIn);
    return () => clearTimeout(timer);
  }, [status, pairingId, grant, grantPending, mintGrant]);

  /**
   * Mint a pairing and put its QR up.
   *
   * The device-link grant that lets a phone with no session sign itself in from the same scan
   * is minted by the refresh effect above, not here. That friction — a password and an MFA code
   * typed into a phone in front of a patient — was the whole thing this feature exists to
   * remove, so paying it to get to the microphone made no sense.
   *
   * ⚠️ THE GRANT IS BEST EFFORT AND THE PAIRING IS NOT. If minting fails — MFA not cleared on
   * this desktop, the callable is down, an anonymous account — the QR still goes up carrying
   * the pairing alone, and a phone that is already signed in (the common case after the first
   * time) never needed the grant anyway. Failing the whole flow because the optional half
   * failed would be the wrong trade.
   *
   * ⚠️ THE TWO HALVES RUN SIDE BY SIDE, AND NEITHER WAITS FOR THE OTHER TO BE ASKED FOR.
   *
   * The grant used to be minted by the refresh effect below, which cannot fire until the
   * pairing write has come back and the status has reached WAITING. So the callable's own
   * latency — measured at 4 to 6 SECONDS on a cold instance, against 200ms warm — started only
   * after a round trip that had nothing to do with it. The mint depends on the signed-in user
   * and nothing else, so it is started here, in parallel, the instant the clinician clicks.
   *
   * The pairing is still awaited: the document must be on the server before the code is on the
   * screen, or the phone's first read misses it. The grant is not awaited here either, because
   * the DIALOG is what waits for it now (see PhoneRecordingDialog) — and that is the part that
   * matters, because a code drawn before the grant lands is a code that signs nobody in.
   *
   * A mint that outlives a failed pairing write is left to expire unspent. Two minutes, never
   * displayed, and the generation bump in reset() means it can never reach a later QR.
   */
  const createPairing = useCallback(async () => {
    if (startingRef.current || !ownerUid) return;
    startingRef.current = true;
    setStarting(true);
    setError(null);

    const gen = (grantGenRef.current += 1);
    setGrant(null);
    setGrantUnavailable(false);
    grantFailuresRef.current = 0;
    mintGrant(gen);

    try {
      const id = await createPhoneRelay({ ownerUid, language });
      pairingIdRef.current = id;

      setPairingId(id);
      statusRef.current = PHONE_RELAY_STATUS.WAITING;
      setStatus(PHONE_RELAY_STATUS.WAITING);
    } catch (err) {
      setError(err?.message || "Could not start phone recording. Please try again.");
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  }, [ownerUid, language, mintGrant]);

  /**
   * Record on the phone — whether or not one is already paired.
   *
   * ⚠️ AN EXISTING PAIRING IS REUSED, NOT REPLACED, and that is the whole point of this
   * function. It used to retire the live pairing and mint a fresh one, so a clinician who had
   * just recorded an encounter was handed a new QR and had to pick their phone back up and
   * scan it again to record the next one — for a phone that was still sitting on the page,
   * still signed in and still listening to the same document.
   *
   * The pairing survives everything except a page load. What a new encounter needs from it is
   * an empty wire and a phone back on its ready screen, which is what rearmPhoneRelay writes.
   *
   * Three cases, in order of how often they happen:
   *   - a phone is paired (connected, or finished a round): re-arm it in place. No QR at all.
   *   - a code is up that nobody has scanned yet: leave it up. Minting a second one would
   *     invalidate the one they are in the middle of scanning.
   *   - nothing live: mint one.
   */
  const start = useCallback(async () => {
    const existing = pairingIdRef.current;

    if (existing) {
      clearStopTimer();
      consumedRoundRef.current = false;
      roundActiveRef.current = false;
      roundBaseRef.current = "";
      setError(null);
      // rearmPhoneRelay nulls the remote on the document, so the desktop has to forget what it
      // last sent or the re-push for the new encounter would be skipped as unchanged.
      lastRemoteJsonRef.current = null;

      // Nobody has scanned it yet, so it is already in exactly the state a new encounter
      // wants. The dialog reopens on the same code.
      if (statusRef.current === PHONE_RELAY_STATUS.WAITING) return;

      try {
        await rearmPhoneRelay(existing);
        statusRef.current = PHONE_RELAY_STATUS.CONNECTED;
        setStatus(PHONE_RELAY_STATUS.CONNECTED);
        return;
      } catch {
        // The only thing that rejects here is a document that is gone: swept, expired, or
        // deleted by another tab. That is not an error to show, it is a pairing to replace.
        pairingIdRef.current = null;
        reset();
      }
    }

    await createPairing();
  }, [clearStopTimer, reset, createPairing]);

  /**
   * Retire the current pairing and put a fresh code up.
   *
   * For the one thing reuse cannot cover: the paired phone is not coming back — it went flat,
   * it went home in someone's pocket, or this encounter is being recorded on a different one.
   * The desktop cannot tell that from a phone that is simply idle, so it is offered rather
   * than guessed at.
   */
  const restart = useCallback(async () => {
    const lingering = pairingIdRef.current;
    if (lingering) {
      pairingIdRef.current = null;
      reset();
      deletePhoneRelay(lingering).catch(() => {
        // The sweep collects it. Never worth failing the new pairing over.
      });
    }
    await createPairing();
  }, [reset, createPairing]);

  /** Cancel before the phone ever joined. */
  const cancel = useCallback(async () => {
    const id = pairingId;
    pairingIdRef.current = null;
    reset();
    if (!id) return;
    try {
      await deletePhoneRelay(id);
    } catch {
      // The sweep collects it. Not worth surfacing.
    }
  }, [pairingId, reset]);

  /**
   * Ask the phone to stop, from the desktop.
   *
   * Writes `stopping` and starts a timer rather than writing `stopped` — the desktop consumes
   * only what the phone confirms, so that its own write cannot race the phone's last flush.
   * See requestPhoneStop in the service.
   */
  const requestStop = useCallback(async () => {
    if (!pairingId) return;
    // ⚠️ ONLY ASK A PHONE THAT IS ACTUALLY RECORDING. A `stopping` written at a phone which has
    // already stopped itself is never answered — the phone's stop handler has nothing left to
    // stop — so it stays on the document and ambushes the NEXT round, which the phone then ends
    // the instant it starts. The two writes crossing is ordinary: the phone's own stop takes as
    // long as its last segments take to transcribe, and the desktop's Stop button is still on
    // screen throughout.
    if (statusRef.current !== PHONE_RELAY_STATUS.RECORDING) return;
    try {
      // Set before the write, because the local echo lands the moment the write is issued.
      selfStopEchoRef.current = true;
      await requestPhoneStop(pairingId);
      // Short window to start with: a phone that never answers at all is dead, backgrounded or
      // offline, and the desktop takes what is on the document. A phone that DOES answer gets
      // the long window instead, in the snapshot handler below.
      armStopTimer(pairingId, PHONE_RELAY_STOP_TIMEOUT_MS);
    } catch (err) {
      // No write, so no echo is coming. Leaving the flag set would swallow the phone's next
      // genuine answer instead.
      selfStopEchoRef.current = false;
      setError(err?.message || "Could not stop the phone recording.");
    }
  }, [pairingId, armStopTimer]);

  /** Push the current clarifying questions down to the phone. Fire and forget. */
  const pushClarifyingQuestions = useCallback(
    (questions) => {
      if (!pairingId) return;
      writeClarifyingQuestions(pairingId, questions).catch(() => {
        // Cosmetic on the phone. Never surface, never block recording.
      });
    },
    [pairingId],
  );

  /**
   * Push the desktop's step, and what its Next would do, down to the phone.
   *
   * Deduplicated here rather than at the call site: the caller is a render-time descriptor that
   * is rebuilt constantly, and a write per render would be hundreds of writes an encounter for
   * a label that changes six times.
   */
  const pushRemoteState = useCallback(
    (remote) => {
      if (!pairingId) return;
      // Canonical, so it can be compared against what a snapshot says the document holds.
      const json = JSON.stringify(buildRemoteState(remote));
      if (json === lastRemoteJsonRef.current) return;
      lastRemoteJsonRef.current = json;
      lastRemoteRef.current = remote;
      remoteWriteInFlightRef.current += 1;
      writeRemoteState(pairingId, remote).finally(() => {
        remoteWriteInFlightRef.current -= 1;
      }).catch((err) => {
        // A stale label on the phone, nothing more — the desktop's own step is authoritative
        // and it re-checks the gate before acting on anything the phone sends back.
        //
        // ⚠️ SAY SO, THOUGH. Swallowing this in silence is how a rules deployment that predates
        // the `remote` key presents: every push is denied, the phone shows no Next button at
        // all, and nothing anywhere reports why.
        console.warn("[chartmind] phone remote push failed", err?.code || err);
        lastRemoteJsonRef.current = null;
      });
    },
    [pairingId],
  );

  // Reached from inside the subscription, which must not re-register every time this callback
  // is rebuilt — the same treatment the ChartMind callbacks above get.
  const pushRemoteRef = useRef(pushRemoteState);
  useEffect(() => {
    pushRemoteRef.current = pushRemoteState;
  }, [pushRemoteState]);

  /** Mark a phone command as dealt with, so no later snapshot replays it. */
  const acknowledgeCommand = useCallback((id) => {
    lastCommandIdRef.current = id;
    setPendingCommand(null);
  }, []);

  // Watch the pairing.
  useEffect(() => {
    if (!pairingId) return undefined;

    const unsubscribe = subscribePhoneRelay(
      pairingId,
      (relay) => {
        if (!relay) {
          // Gone from under us (swept, or deleted in another tab). Nothing left to consume,
          // and nothing left to delete either.
          pairingIdRef.current = null;
          reset();
          return;
        }

        statusRef.current = relay.status;
        setStatus(relay.status);
        setPhoneClaimedAt(relay.phoneClaimedAt);
        latestTranscriptRef.current = relay.transcript;

        // ⚠️ BEFORE THE TRANSCRIPT BELOW, NOT AFTER IT. A snapshot can carry both the claim and
        // a first flush, and composing that flush against last round's base would repeat the
        // previous encounter at the top of this one.
        if (relay.status === PHONE_RELAY_STATUS.RECORDING && !roundActiveRef.current) {
          roundActiveRef.current = true;
          consumedRoundRef.current = false;
          // Target first: the base depends on it. An encounter round appends to the encounter, a
          // section round to what is already written in that section.
          roundTargetRef.current = getRoundTargetRef.current?.() || { kind: "encounter" };
          roundBaseRef.current =
            getBaseTranscriptRef.current?.(roundTargetRef.current) || "";
        }

        /**
         * ⚠️ ONLY WHILE A ROUND IS ACTUALLY RUNNING. Outside a round the wire is either text
         * this desktop has already consumed or text left over from a round it could not blank,
         * and `roundTargetRef` still names wherever the LAST round was going — so routing it
         * poured the previous round's words into whatever the next round is armed for. That is
         * how an encounter recorded on the Record step turned up pasted into a note section the
         * moment the phone was armed to dictate into it, a second before the real text arrived.
         *
         * A claim and a first flush can still share one snapshot: the round is opened just
         * above, so `roundActiveRef` is already true by the time this runs.
         */
        if (relay.transcript && roundActiveRef.current) {
          onTranscriptRef.current?.(composeRound(relay.transcript), roundTargetRef.current);
        }

        /**
         * ⚠️ DOES THE DOCUMENT ACTUALLY HOLD WHAT WE LAST SENT?
         *
         * The push is deduplicated against what this tab BELIEVES it sent, which is a guess
         * about a server. Anything that makes that guess wrong — a write that never landed, a
         * re-arm that nulled the remote, another tab writing to the same pairing — leaves the
         * desktop convinced the phone is up to date while the phone shows a step the clinician
         * left a minute ago, and nothing re-sends because the dedupe says there is nothing to
         * send. Forgetting the guess is enough: the push effect re-runs on the next render and
         * writes the current remote again.
         *
         * Only once our own writes have settled, or this would fight a write still in flight
         * and send it twice.
         */
        if (
          remoteWriteInFlightRef.current === 0 &&
          lastRemoteJsonRef.current !== null &&
          JSON.stringify(buildRemoteState(relay.remote)) !== lastRemoteJsonRef.current
        ) {
          const intended = lastRemoteRef.current;
          lastRemoteJsonRef.current = null;
          pushRemoteRef.current?.(intended);
        }

        // A press of the phone's Next. Held until the desktop's own gate has been re-checked
        // against its current state — see ChartMindPage. The id is what stops a replayed
        // snapshot advancing the same step twice.
        if (relay.command?.verb && relay.command.id !== lastCommandIdRef.current) {
          setPendingCommand({ verb: relay.command.verb, id: relay.command.id });
        }

        if (relay.status === PHONE_RELAY_STATUS.STOPPING && stopTimerRef.current) {
          // ⚠️ THE PHONE ANSWERED, SO STOP COUNTING IT DEAD. A snapshot landing while a stop is
          // outstanding means the phone is alive and draining the segments still out for
          // transcription, and the five-second window it was being held to would cut that off
          // and take a transcript missing its last few seconds. Hand it the long window instead.
          //
          // ⚠️ EXCEPT OUR OWN `stopping`, WHICH IS NOT AN ANSWER. This used to lean on the
          // subscription dropping snapshots with pending writes — which it no longer does,
          // because that is what silently ate the other device's changes (see
          // subscribePhoneRelay). So the exception is stated here instead of being a side
          // effect of how the listener was configured. One echo, consumed once: a phone that
          // never answers at all still hits the short timeout it is supposed to.
          if (selfStopEchoRef.current) {
            selfStopEchoRef.current = false;
          } else {
            armStopTimer(pairingId, PHONE_RELAY_STOP_FLUSH_MS);
          }
        }

        if (relay.status === PHONE_RELAY_STATUS.STOPPED && !consumedRoundRef.current) {
          // ⚠️ CONSUME, BUT DO NOT TEAR THE PAIRING DOWN. The round is over; the wire is not.
          // The clinician may tap record again on the phone they are still holding, or start
          // the next encounter from this desktop, and neither should cost them a scan.
          consumedRoundRef.current = true;
          roundActiveRef.current = false;
          onFinishedRef.current?.(composeRound(relay.transcript || ""), {
            timedOut: false,
            target: roundTargetRef.current,
          });
          // ⚠️ CLEAR THE STOP TIMER TOO. When the stop came from the desktop, requestStop armed
          // a five-second "the phone never answered" timer. The phone HAS now answered, so
          // leaving it running would fire the timeout path over a round already consumed.
          clearStopTimer();
          // The desktop holds this text now, so it has no business staying on a server. The
          // base keeps it: a late straggler flush from this same round still composes correctly
          // against it, and the next round re-reads the base from the session anyway.
          latestTranscriptRef.current = "";
          clearRelayTranscript(pairingId).catch(() => {
            // It expires in two hours regardless, and the sweep collects it. Not worth an
            // error state over — the encounter itself is already safely on the desktop.
          });
        }
      },
      (err) => setError(err?.message || "Lost contact with the phone."),
      // ⚠️ THE PHONE'S PRESS MUST NOT BE DROPPED BECAUSE THIS TAB HAPPENED TO BE WRITING. The
      // desktop writes constantly — a remote on every step change, clarifying questions while
      // recording, a transcript clear the moment a round lands, which is the exact moment the
      // clinician reaches for the phone's Next button. Any of those in flight used to mean the
      // snapshot carrying `command` was discarded and never re-sent. See subscribePhoneRelay.
      { includeLocalWrites: true },
    );

    return () => unsubscribe?.();
  }, [pairingId, reset, clearStopTimer, armStopTimer, composeRound]);

  /**
   * Give up on a phone that stopped answering, on an effect of its own.
   *
   * ⚠️ THIS CANNOT LIVE IN THE SNAPSHOT CALLBACK. Consuming means clearing `pairingId`, which is
   * the subscription effect's dependency, so React tears that effect down and runs its cleanup
   * synchronously the moment we do — while an awaited delete inside it would still be in flight.
   * The same class of bug LinkDevicePage hit and documented: a mocked call resolves in a
   * microtask and wins the race, so it only ever appears against a real network.
   *
   * ⚠️ AND THIS ONE DOES DELETE THE PAIRING, unlike the ordinary stop above. Reaching here means
   * the phone went quiet mid-stop, so the desktop is taking a transcript the phone never
   * confirmed and cannot blank the wire with any confidence about what is still coming. A dead
   * pairing is the honest state, and the phone lands on the expired screen that says so.
   */
  useEffect(() => {
    if (!finalPayload) return undefined;

    const { pairingId: id, transcript, timedOut } = finalPayload;
    const finalText = composeRound(transcript ?? latestTranscriptRef.current);

    clearStopTimer();
    // Claim the id before anything async, so the unmount handler above cannot also delete it.
    pairingIdRef.current = null;
    // With the round's own target, not without one: an omitted target reads as the encounter,
    // so a dictation round that timed out used to write its addendum over the encounter
    // transcript instead of into the section it was spoken for.
    if (finalText) onTranscriptRef.current?.(finalText, roundTargetRef.current);
    onFinishedRef.current?.(finalText || "", { timedOut, target: roundTargetRef.current });
    reset();

    deletePhoneRelay(id).catch(() => {
      // Delete-after-read: the sweep collects a leftover, so this is not worth an error state.
    });

    return undefined;
  }, [finalPayload, clearStopTimer, reset, composeRound]);

  useEffect(() => clearStopTimer, [clearStopTimer]);

  /**
   * Do not leave a pairing behind when this tab goes away.
   *
   * WHY THIS IS NOT JUST TIDINESS. A pairing mid-round holds a verbatim transcript, and for
   * Kijabe beta and admin-simulation users it is the only place that text ever reaches a
   * server — their sessions are never persisted. Waiting up to two hours for the sweep to
   * collect an abandoned pairing is the difference between "transient" and merely "expiring",
   * and transient is what was promised. Between rounds the wire is already blank, so what this
   * collects is usually an empty pointer — which is exactly why a pairing can now be allowed to
   * outlive the round that used it.
   *
   * Deleting mid-recording is the right call even though it ends the phone's recording: if
   * this tab is gone, nothing will ever consume that transcript anyway. The phone sees the
   * document vanish and stops, which is already handled on its side.
   *
   * ⚠️ beforeunload is BEST EFFORT and nothing more. A Firestore delete issued there usually
   * does not get to flush before the tab dies, and browsers give no way to await it. The
   * scheduled sweep remains the actual guarantee — see
   * functions/src/scheduled/chartMindPhoneCleanup.js. This just closes the common case (a
   * clinician who closes the tab or signs out) so the sweep is the exception rather than the
   * mechanism.
   *
   * Kept in a ref so this effect does not tear down and re-register on every status change.
   */
  useEffect(() => {
    const abandon = () => {
      const id = pairingIdRef.current;
      if (!id) return;
      pairingIdRef.current = null;
      // Belt and braces: this runs inside a React cleanup and inside beforeunload, and a throw
      // in either is worse than a leftover document the sweep will collect. Promise.resolve
      // covers a delete that rejects; the try/catch covers one that throws synchronously.
      try {
        Promise.resolve(deletePhoneRelay(id)).catch(() => {});
      } catch {
        // The sweep collects it.
      }
    };

    window.addEventListener("beforeunload", abandon);
    return () => {
      window.removeEventListener("beforeunload", abandon);
      // Unmount is the reliable half: leaving the dashboard, signing out, a route teardown.
      abandon();
    };
  }, []);

  return {
    pairingId,
    grant,
    grantUnavailable,
    status,
    error,
    starting,
    phoneClaimedAt,
    isPhoneRecording:
      status === PHONE_RELAY_STATUS.RECORDING || status === PHONE_RELAY_STATUS.STOPPING,
    // Still showing a code nobody has scanned.
    isPairing: status === PHONE_RELAY_STATUS.WAITING,
    // Scanned, signed in, and waiting for the clinician to tap record on the phone. Also where
    // a paired phone sits between encounters, which is why this is the state re-arming returns
    // a pairing to rather than a fresh code.
    isPhoneConnected: status === PHONE_RELAY_STATUS.CONNECTED,
    start,
    restart,
    cancel,
    requestStop,
    pushClarifyingQuestions,
    pushRemoteState,
    pendingCommand,
    acknowledgeCommand,
    clearError: () => setError(null),
  };
};

export default usePhoneRecordingRelay;
